import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useEvent from 'react-use-event-hook'

import { logSentry } from '@gto/sentry'
import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import { AppDialog } from '@/components/app-dialog'
import { CopyButton } from '@/components/copy-button'
import { type AffiliateAdminCouponSchema, buildCouponBatchCsv } from '@/domain/coupons'
import { downloadBlob } from '@/utils/download-blob'

type CouponBatchResultsDialogProps = {
  open: boolean
  coupons: AffiliateAdminCouponSchema[]
  onClose: () => void
}

/** Post-batch delivery: the generated codes with Copy all + Download CSV. */
export const CouponBatchResultsDialog = ({
  open,
  coupons,
  onClose,
}: CouponBatchResultsDialogProps) => {
  const { formatMessage } = useIntl()
  const [copied, setCopied] = useState(false)

  const codes = coupons.map((coupon) => coupon.code)

  const handleCopyAll = useEvent(() => {
    navigator.clipboard
      .writeText(codes.join(', '))
      .then(() => {
        setCopied(true)
        // Revert the "Copied!" label after a beat (mirrors CopyButton) so the
        // button doesn't stay latched for the life of the dialog.
        setTimeout(() => setCopied(false), 2000)
      })
      // Surface a clipboard failure to Sentry — never a swallowed catch — so a
      // blocked permission is observable rather than a button that silently
      // does nothing.
      .catch((error: unknown) => logSentry('coupon batch copy-all failed: ', error))
  })

  const handleDownloadCsv = () => {
    const blob = new Blob([buildCouponBatchCsv(coupons)], { type: 'text/csv;charset=utf-8;' })
    downloadBlob(blob, `affiliate-links-${new Date().toISOString().slice(0, 10)}.csv`)
  }

  return (
    <AppDialog
      open={open}
      width={480}
      onClose={onClose}
    >
      <AppDialog.Header
        subtitle={formatMessage({ id: 'coupon.batch.resultsSubtitle' }, { count: coupons.length })}
        title={formatMessage({ id: 'coupon.batch.resultsTitle' })}
      />
      <AppDialog.Body>
        <div
          className="max-h-72 space-y-1 overflow-y-auto rounded-lg bg-paper-elevation3 p-3"
          data-tst="coupon-batch-codes"
        >
          {coupons.map((coupon) => (
            <div
              key={coupon.id}
              className="flex flex-row items-center justify-between gap-2"
            >
              <Typography className="font-mono text-sm">{coupon.code}</Typography>
              <CopyButton text={coupon.code} />
            </div>
          ))}
        </div>
      </AppDialog.Body>
      <AppDialog.Footer>
        <Button onClick={handleDownloadCsv}>
          <FormattedMessage id="coupon.batch.downloadCsv" />
        </Button>
        <Button
          variant="contained"
          onClick={handleCopyAll}
        >
          {copied ? (
            <FormattedMessage id="coupons.copied" />
          ) : (
            <FormattedMessage id="coupon.batch.copyAll" />
          )}
        </Button>
      </AppDialog.Footer>
    </AppDialog>
  )
}
